/**
 * «Юниверситет» (28.09): прогон — задания по порядку из пакета (не перемешиваются; pick
 * макета не используется), после ответа — отзыв «Верно./Неверно.» + пояснение и «Дальше»,
 * на последнем — «Итог». Для вопросов дня каждый ответ сохраняется сразу (onAnswer),
 * нумерация — абсолютная (после продолжения не «1 / 2», а «2 / 3»).
 */
import { useEffect, useRef, useState, type ReactNode } from "react";
import type { UniPack } from "@ub/shared/uni";
import { ItemView } from "./ItemView";
import { Rich } from "./rich";
import css from "./run.module.css";
import ui from "./uni.module.css";

interface RunnerProps {
  pack: UniPack;
  title: string;
  /** id заданий по порядку */
  ids: string[];
  /** абсолютные номера заданий (вопросы дня) и сколько их всего */
  numbers?: number[];
  total?: number;
  onAnswer?: (id: string, ok: boolean) => void;
  /** конец прогона — вызывается один раз, по кнопке «Итог» */
  onEnd?: (got: number, n: number) => void;
  result: (got: number, n: number) => ReactNode;
  /** прокрутить шторку к началу (новое задание) */
  onTop: () => void;
}

export function Runner({ pack, title, ids, numbers, total, onAnswer, onEnd, result, onTop }: RunnerProps) {
  const [list] = useState(() => ids.filter((id) => !!pack.items[id]));
  const [i, setI] = useState(0);
  const [got, setGot] = useState(0);
  const [fb, setFb] = useState<{ ok: boolean; extra: string } | null>(null);
  const [end, setEnd] = useState(false);
  const nextRef = useRef<HTMLButtonElement>(null);

  /* ответ дан — фокус на «Дальше» (варианты стали disabled, фокус иначе теряется) */
  useEffect(() => { if (fb) nextRef.current?.focus(); }, [fb]);

  if (!list.length) return <p className={css.resText}>Задания не найдены — напиши ОО.</p>;
  if (end) return <>{result(got, list.length)}</>;

  const id = list[i]!;
  const item = pack.items[id]!;
  const last = i + 1 >= list.length;

  const answered = (ok: boolean, extra: string) => {
    if (fb) return;
    setFb({ ok, extra });
    if (ok) setGot((g) => g + 1);
    onAnswer?.(id, ok);
  };
  const next = () => {
    if (last) {
      setEnd(true);
      onEnd?.(got, list.length);
    } else {
      setI(i + 1);
      setFb(null);
    }
    onTop();
  };

  return (
    <>
      <div className={css.meta} data-fb-part="шапка задания">
        <span>{title}</span>
        <span>{numbers?.[i] ?? i + 1} / {total ?? list.length}</span>
      </div>
      {/* 16.10 замечания: всё задание — один якорь (display: contents — вёрстка шторки не меняется) */}
      <div style={{ display: "contents" }} data-fb={`item:${id}`} data-fb-name={`задание ${numbers?.[i] ?? i + 1}/${total ?? list.length}`}>
      <ItemView key={`${i}:${id}`} item={item} onDone={answered} />
      <div aria-live="polite">
        {fb ? (
          <>
            <div className={`${css.fb} ${fb.ok ? css.fbOk : css.fbBad}`} data-fb-part="пояснение после ответа">
              <strong>{fb.ok ? "Верно." : "Неверно."}</strong>{" "}
              <Rich text={fb.extra} />
              <Rich text={item.explain} />
            </div>
            <div className={ui.acts}>
              <button ref={nextRef} type="button" className={ui.btn} onClick={next}>{last ? "Итог" : "Дальше"}</button>
            </div>
          </>
        ) : null}
      </div>
      </div>
    </>
  );
}
